import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useParams } from "react-router";

import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { getEmailPrefs, updateEmailPrefs } from "../../api";
import { BlockInput, DataForm, setDataFormField } from "../../components";
import { FieldType, modalActions } from "../../constants";
import { UserEmailPrefsDto } from "../../types/dto";
import { ModalSetting } from "../../types/setting";

export const EmailPrefs = () => {
	const { email, code } = useParams() as { email: string; code: string };
	const urlParams = new URLSearchParams(window.location.search);
	const backUrl = decodeURIComponent(urlParams.get("backUrl") || "");

	const [prefs, setPrefs] = useState<UserEmailPrefsDto>();

	const [isLoading, setIsLoading] = useState(true);
	const [errors, setErrors] = useState<any>({});

	const refreshPrefs = async () => {
		setIsLoading(true);

		try {
			var res = await getEmailPrefs({
				code: decodeURIComponent(code),
				email: decodeURIComponent(email),
			});
			setPrefs(res);
		} catch (ex) {
			setErrors(ex);
		} finally {
			setIsLoading(false);
		}
	};

	const handleFormSubmit = async () => {
		setIsLoading(true);

		try {
			if (!prefs) {
				console.error(
					"Attempted to update email prefs, but prefs was null"
				);
				return;
			}

			await updateEmailPrefs({
				code: decodeURIComponent(code),
				email: decodeURIComponent(email),
				prefs: prefs,
			});
			// await refreshPrefs();
			if (backUrl) window.location.replace(backUrl);
		} catch (ex) {
			setErrors(ex);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		refreshPrefs();
	}, [code]);

	const form = useMemo(() => {
		if (!prefs) return;

		const fields = [
			[
				{
					field: "notifyUsersAboutNewLicence",
					title: "Get notified when you are issued new licences",
					type: FieldType.CHECKBOX,
				},
			],
			[
				{
					field: "notifyUsersAboutSetLicenceState",
					title: "Get notified when your licences are revoked or reinstated",
					type: FieldType.CHECKBOX,
				},
			],
		];
		if (prefs["notifyManagersAboutLicenceIssued"] != null) {
			fields.push([
				{
					field: "notifyManagersAboutLicenceIssued",
					title: "Get notified when a licence is issued for a subscription you manage",
					type: FieldType.CHECKBOX,
				},
			]);
		}
		if (prefs["notifyAboutSubActivation"] != null) {
			fields.push([
				{
					field: "notifyAboutSubActivation",
					title: "Get notified when a customer activates a subscription for a product you manage",
					type: FieldType.CHECKBOX,
				},
			]);
		}

		const formSetting: ModalSetting = {
			action: modalActions.EDIT,
			fields: fields,
		};

		return (
			<DataForm
				editorSetting={formSetting}
				currentFormData={prefs}
				setFormData={setDataFormField(setPrefs)}
				errors={errors}
			/>
		);
	}, [prefs]);

	return (
		<main>
			<section className="vh-100 d-flex align-items-center justify-content-center">
				<Row>
					<Col lg={12} xs={12}>
						{backUrl ? (
							<a
								className="text-gray-700 card-link"
								href={backUrl}
							>
								<FontAwesomeIcon
									icon={faAngleLeft}
									className="me-2"
								/>
								Back
							</a>
						) : (
							""
						)}
						<Card>
							<BlockInput blocking={isLoading} />
							<Card.Header>
								<h2 className="text-primary">
									Change Email Preferences For{" "}
									{`<${decodeURIComponent(email)}>`}
								</h2>
							</Card.Header>
							<Card.Body>{form}</Card.Body>
							<Card.Footer className="pt-0 border-0">
								<Button
									className="float-end"
									variant="secondary"
									onClick={handleFormSubmit}
								>
									Save
								</Button>
							</Card.Footer>
						</Card>
					</Col>
				</Row>
			</section>
		</main>
	);
};
